<template>
  <div>
    <el-aside
      width="200px"
      class="side-navigator">
      <div
        class="nav-bar-logo"
        @click="$router.push('/')">
        <div class="helper"/>
        <img src="@/assets/img/grocery-tv-logo-charcoal.svg" >
      </div>
      <el-menu
        ref="sideNavigation"
        :router="true"
        :default-active="$route.path"
        :unique-opened="true"
        class="side-menu"
        @open="handleSubmenuToggle"
        @close="handleSubmenuToggle">

        <!-- Product Views -->
        <el-menu-item-group
          v-if="hasPermission('role:advertiser')
            || hasPermission('role:vendor')
            || hasPermission('role:retailer')
            || hasPermission('role:wholesaler')
            || hasPermission('role:super_admin')
            || hasPermission('role:device_install_contractor')
          || hasPermission('role:it')">
          <el-menu-item
            v-if="hasPermission('role:retailer')
              || hasPermission('role:vendor')
            || hasPermission('role:super_admin')"
            class="side-navigator__top-level-item"
            index="/">
            <template slot="title">
              <navigation-icon
                name="home"/>
              Home
            </template>
          </el-menu-item>

          <marketing-navigation-group
            :sidebar-entries="sidebarEntries"/>
          <merchandise-navigation-group
            :sidebar-entries="sidebarEntries"/>
          <management-navigation-group
            :sidebar-entries="sidebarEntries"/>

        </el-menu-item-group>

        <!-- Internal Views -->
        <el-menu-item-group>
          <stockcheck-navigation-group/>
          <stores-navigation-group/>
          <products-navigation-group/>
          <devices-navigation-group/>
        </el-menu-item-group>
        <el-submenu
          v-if="hasPermission('feature_group:content_review')"
          index="6">
          <template slot="title">
            <span>Content Review</span>
            <el-tooltip
              content="Content is ready for review">
              <el-badge
                :hidden="!totalReviewCount"
                :is-dot="true"
                :value="totalReviewCount"
                class="content-badge"/>
            </el-tooltip>
          </template>
          <el-menu-item
            v-if="hasPermission('feature:content_review:campaigns')"
            class="side-navigator__menu-item"
            index="/review/campaign">
            Campaigns
            <el-badge
              :hidden="!unreviewedCampaigns.length"
              :value="unreviewedCampaigns.length"
              class="content-pill-badge"/>
          </el-menu-item>
          <el-menu-item
            v-if="hasPermission('feature:content_review:videos')"
            class="side-navigator__menu-item"
            index="/review/video">
            Videos
            <el-badge
              :hidden="!totalUnreviewedVideos"
              :value="totalUnreviewedVideos"
              class="unreviewedBadge-item"/>
          </el-menu-item>
          <el-menu-item
            v-if="hasPermission('feature:content_review:branding')"
            class="side-navigator__menu-item"
            index="/review/branding">
            Images
            <el-badge
              :hidden="!unreviewedImages.length"
              :value="unreviewedImages.length"
              class="unreviewedBadge-item"/>
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </el-aside>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import 'vue-awesome/icons/eye-slash'

import SidebarEntriesQuery from '@/graphql/sidebar/SidebarEntries.gql'

import DevicesNavigationGroup from './DevicesNavigationGroup'
import ManagementNavigationGroup from './ManagementNavigationGroup'
import MarketingNavigationGroup from './MarketingNavigationGroup'
import MerchandiseNavigationGroup from './MerchandiseNavigationGroup'
import ProductsNavigationGroup from './ProductsNavigationGroup'
import StockcheckNavigationGroup from './StockcheckNavigationGroup'
import StoresNavigationGroup from './StoresNavigationGroup'
import NavigationIcon from './NavigationIcon'

export default {
  name: 'SideNavigator',
  components: {
    DevicesNavigationGroup,
    ManagementNavigationGroup,
    MarketingNavigationGroup,
    MerchandiseNavigationGroup,
    NavigationIcon,
    ProductsNavigationGroup,
    StockcheckNavigationGroup,
    StoresNavigationGroup,
  },
  data() {
    return {
      sidebarEntries: null,
    }
  },
  computed: {
    ...mapGetters([
      'totalUnreviewedVideos',
      'unreviewedImages',
    ]),
    darkMode: {
      set(val) {
        return this.$store.commit('DarkMode/setMode', val)
      },
      get() {
        return this.$store.state.DarkMode.enabled
      },
    },
    unreviewedCampaigns() {
      return this.$store.state.CampaignList.unreviewedCampaigns
    },
    totalReviewCount() {
      return this.totalUnreviewedVideos
        + this.unreviewedImages.length
        + this.unreviewedCampaigns.length
    },
  },
  created() {
    this.$apollo.query({
      query: SidebarEntriesQuery,
    }).then((res) => {
      this.sidebarEntries = res.data.sidebarEntries
    })
  },
  mounted() {
    /**
     * These queries don't need to happen until after the
     * content on the page loads
     */
    setTimeout(() => {
      if (this.hasPermission('feature_group:content_review')) {
        this.$store.dispatch('loadUnreviewedVideos', { offset: 0, limit: 1, totalOnly: true })
        this.$store.dispatch('loadUnreviewedImages')
        this.$store.dispatch('CampaignList/loadUnreviewedCampaigns')
      }
    }, 3000)
  },
  methods: {
    /**
     * Updated workaround for linking top-level
     * submenus to specific routes.
     *
     * Requires the index of the submenu to be
     * registered here. Element's sidebar menu
     * uses the index as a ref to the appropriate
     * submenu.
     */
    handleSubmenuToggle(index) {
      switch (index) {
        case '/marketing':
          this.$router.push('/marketing')
          break
        case '/merchandise':
          this.$router.push('/merchandise')
          break
        default:
          break
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/styles/base/_animation.scss';
@import '@/assets/styles/base/_color.scss';

.content-badge .el-badge__content.is-dot {
  animation: pulse 2s infinite;
  background-color: $color-caribbean-green;
  margin-left: 2px;
}
.content-pill-badge .el-badge__content {
  width: 14px;
  background-color: $color-caribbean-green;
  margin-left: 5px;
}
</style>

<style lang="scss" scoped>
@import '@/assets/styles/base/_color.scss';

.side-navigator__menu-group-header {
  text-decoration: dotted;
}

.el-aside.side-navigator .nav-bar-logo {
  display: block;
  height: 69px;
  margin-top: 10px;
  text-align: center;
  width: 100%;
  img {
    pointer-events: none;
  }
}

.nav-bar-logo .helper {
  display: inline-block;
  height: 100%;
  vertical-align: middle;
  width: 0;
}

.el-aside.side-navigator .nav-bar-logo img {
  display: inline-block;
  margin-left: auto;
  margin-right: auto;
  vertical-align: middle;
  width: 50%;
}

.el-menu-item.is-active {
  font-weight: 600;
}

.el-submenu.is-active .el-submenu__title {
  font-weight: 600;

  border-left: 4px solid #03c7a4;
}

.nav-icon-wrapper {
  width: 15px;
}

.el-menu-item.is-active .nav-icon {
  margin-left: -4px;
}

.unreviewedBadge-submenu {
  margin-bottom: 2px;
}

.unreviewedBadge-item {
  margin-bottom: 3px;
}
</style>

<style lang="scss">
@import '@/assets/styles/base/_color.scss';
.menu-group-title {
  color: #8297aa;
  font-weight: 700;
  text-transform: uppercase;
  padding-left: 45px;
  letter-spacing: 0.1em;
  font-size: 10pt;
  margin-bottom: 10px;
}

.active-submenu .nav-icon {
  margin-left: -4px;
}
</style>



// WEBPACK FOOTER //
// src/components/navigation/SideNavigator.vue